//
// Copyright (c) 2019-Present Syncari - All rights reserved.
//
import React from 'react';
import { Icon, Tooltip } from 'antd';
import cx from 'classnames';

import FieldTypeBadge from 'components/FieldTypeBadge';

import { getDataTypeFromValue } from '../utils/fuzzyValueHelpers';
import { FuzzyOptionProps } from '../types';
import FuzzySliderPanel from './FuzzySliderPanel';

/**
 * FuzzyOption component represents a single option in the fuzzy select dropdown
 * Handles both regular options and fuzzy options with sliders
 */
const FuzzyOption: React.FC<FuzzyOptionProps> = ({
  option,
  isFuzzy,
  isExpanded,
  isSelected,
  useDataTypeBadges,
  expandedFuzzyOption,
  onSelect,
  onSliderChange,
}) => {
  const { value, label } = option;
  const dataType = getDataTypeFromValue(option);

  return (
    <div
      key={value}
      className={cx('fuzzy-select-option-wrapper', {
        'fuzzy-select-option-wrapper--expanded': isExpanded,
      })}
      data-testid={`fuzzy-option-${value}`}>
      <div
        className={cx('fuzzy-select-option', {
          'fuzzy-select-option--fuzzy': isFuzzy,
          'fuzzy-select-option--expanded': isExpanded,
          'fuzzy-select-option--selected': isSelected,
        })}
        onClick={(e) => {
          e.preventDefault();
          onSelect(option, option?.hasFuzzyOption ?? false);
        }}
        data-testid={`fuzzy-option-button-${value}`}>
        {useDataTypeBadges && dataType ? (
          <div className="fuzzy-select-option-content">
            <FieldTypeBadge dataType={dataType} description={dataType} size="small" />
            <span className="fuzzy-select-option-label">
              <Tooltip title={`[${value}]`}>{label}</Tooltip>
            </span>
          </div>
        ) : (
          <span className="fuzzy-select-option-label">
            {label}
            {isFuzzy && (
              <Tooltip title={`[${value}]`}>
                <span className="fuzzy-select-option-tag">[{value}]</span>
              </Tooltip>
            )}
          </span>
        )}
        {isExpanded && <Icon type="check" className="fuzzy-select-option-check-icon" />}
      </div>

      {/* Expanded slider panel */}
      {isExpanded && expandedFuzzyOption && (
        <FuzzySliderPanel
          option={option}
          percentage={expandedFuzzyOption.percentage}
          typoCount={expandedFuzzyOption.typoCount}
          isExpanded={isExpanded}
          onSliderChange={onSliderChange}
        />
      )}
    </div>
  );
};

export default FuzzyOption;
